import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withConfig from '../../../context/withConfig';
import { sendTextMessage } from '../../../reducers/history/actions';
import { toggleChatPanelVisible } from '../../../reducers/ui/actions';
import MinimizeIcon from '../../icons/MinimizeIcon';
import { visibleActivities } from '../../../reducers/history/selectors';
import withIsMobile from '../../../context/withIsMobile';

const ANIMATION_DURATION = '0.5s';
const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const LoadingDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: currentColor;
  display: inline-block;
  margin: 0 2px;
  animation: ${bounce} 1.2s infinite ease-in-out;
  animation-delay: ${props => props.delay}s;
`;

const LoadingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
`;

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.backgroundColor || '#F5F5F5'};
  z-index: 1000;
  margin: ${props => (props.isMobile ? '0' : '15px')};
  border-radius: ${props => (props.isMobile ? '0' : '16px')};
  opacity: ${props => (props.show ? 1 : 0)};
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  transition: all ${ANIMATION_DURATION} ease-out;
  animation: ${props => (!props.show ? fadeOut : 'none')} ${ANIMATION_DURATION} ease-out forwards;
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  padding: 32px 24px;
  box-sizing: border-box;
  height: 100%;

  ${props =>
    props.isMobile &&
    `
    padding: 24px 16px;
  `};
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: ${props => (props.showResumeButton ? 0 : '20px')};
  transition: transform ${ANIMATION_DURATION} ease-out;
  transform: translateY(${props => (props.showResumeButton ? '-20px' : '0')});
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const Title = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: ${props => props.theme.introTitleColor};
`;

const Subtitle = styled.div`
  font-size: 24px;
  color: ${props => props.theme.introSubtitleColor};
  font-weight: normal;
`;

const Button = styled.button`
  background: ${props =>
    props.primary ? props.theme.introPrimaryButtonColor : props.theme.introSecondaryButtonColor};
  color: ${props =>
    props.primary
      ? props.theme.introPrimaryButtonTextColor
      : props.theme.introSecondaryButtonTextColor};
  padding: 16px 24px;
  border-radius: 20px;
  border: none;
  font-size: 18px;
  margin-bottom: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    background: ${props =>
      props.primary ? props.theme.introPrimaryButtonColor : props.theme.introSecondaryButtonColor};
  }

  svg {
    width: 24px;
    height: 24px;
    margin-left: 16px;
  }
`;

const ResumeButton = styled(Button)`
  display: ${props => (props.show ? 'flex' : 'none')};
  animation: ${props => (props.show ? slideIn : 'none')} ${ANIMATION_DURATION} ease-out;
  position: relative;
`;

const Card = styled.div`
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  display: flex;
  min-height: 117px;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${props => props.image});
  background-size: cover;
  background-position: center;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  transition: transform 0.2s ease;

  &:hover {
    transform: ${props => (props.onClick ? 'scale(1.02)' : 'none')};
  }
`;

const CardContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1;
`;

const CardTitle = styled.h3`
  font-size: 19px;
  color: ${props => props.theme.introCardTextColor};
  margin: 0 0 8px 0;
  font-weight: bold;
`;

const CardText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.introCardTextColor};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const ArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IntroOverlay = ({
  show,
  onCloseIntro,
  isMobile,
  config,
  sendTextMessage,
  toggleChatPanelVisible,
  hasHistory,
  connectionStatus
}) => {
  const [hasPreviousConversation, setHasPreviousConversation] = useState(false);
  const [showResumeButton, setShowResumeButton] = useState(false);

  // Check if has previous conversation each time Intro is displayed
  useEffect(
    () => {
      if (show && hasHistory) {
        setHasPreviousConversation(hasHistory);
        setShowResumeButton(false);

        const timer = setTimeout(() => {
          setShowResumeButton(hasHistory);
        }, 500);

        return () => {
          clearTimeout(timer);
          setShowResumeButton(false);
        };
      }

      return undefined;
    },
    [show, hasHistory]
  );

  const handleStartNewChat = () => {
    const event = new MessageEvent('message', {
      data: {
        type: 'createNewUser'
      }
    });

    window.dispatchEvent(event);
    onCloseIntro();
  };

  const handleResumeChat = () => {
    onCloseIntro();
  };

  const handleShortcutClick = () => {
    sendTextMessage({ text: config.strings.introShortcutCommand });

    // Close the intro overlay
    onCloseIntro();
  };

  const handleClose = () => {
    toggleChatPanelVisible();
  };

  const { strings, images, theme } = config;

  const overlayStyle = images.introBackground
    ? {
        backgroundImage: `url(${images.introBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        background: `linear-gradient(to bottom, ${theme.introBackgroundColor || '#f2f2f2'}, white)`
      };

  const isReady = connectionStatus === 2;

  if (!show) return null;

  return (
    <OverlayContainer show={show} isMobile={isMobile} style={overlayStyle}>
      {show && (
        <React.Fragment>
          <MinimizeIcon onClick={handleClose} />
          <Content isMobile={isMobile}>
            <HeaderSection showResumeButton={showResumeButton && hasPreviousConversation}>
              <Title>{strings.introTitle}</Title>
              <Subtitle>{strings.introSubtitle}</Subtitle>
            </HeaderSection>

            <ButtonSection>
              {hasPreviousConversation && (
                <ResumeButton primary show={showResumeButton} onClick={handleResumeChat}>
                  Resume chat
                  <ArrowIcon />
                </ResumeButton>
              )}

              <Button onClick={handleStartNewChat} disabled={!isReady}>
                Start a new chat
                {!isReady && (
                  <LoadingIndicator>
                    <LoadingDot delay={0} />
                    <LoadingDot delay={0.2} />
                    <LoadingDot delay={0.4} />
                  </LoadingIndicator>
                )}
                {isReady && <ArrowIcon />}
              </Button>

              {strings.introShortcutName && (
                <Button onClick={handleShortcutClick} disabled={!isReady}>
                  {strings.introShortcutName}
                  {!isReady && (
                    <LoadingIndicator>
                      <LoadingDot delay={0} />
                      <LoadingDot delay={0.2} />
                      <LoadingDot delay={0.4} />
                    </LoadingIndicator>
                  )}
                  {isReady && <ArrowIcon />}
                </Button>
              )}
            </ButtonSection>

            <Card
              image={images.introCardBackground}
              onClick={
                strings.introCardLink
                  ? () => window.open(strings.introCardLink, '_blank')
                  : undefined
              }
            >
              <CardContent>
                <CardTitle>{strings.introCardTitle}</CardTitle>
                <CardText>{strings.introCardInfo}</CardText>
              </CardContent>
            </Card>
          </Content>
        </React.Fragment>
      )}
    </OverlayContainer>
  );
};

IntroOverlay.propTypes = {
  show: PropTypes.bool.isRequired,
  onCloseIntro: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  config: PropTypes.object,
  sendTextMessage: PropTypes.func.isRequired,
  toggleChatPanelVisible: PropTypes.func.isRequired,
  hasHistory: PropTypes.bool.isRequired,
  connectionStatus: PropTypes.number.isRequired
};

const mapStateToProps = state => ({
  hasHistory: visibleActivities(state).filter(a => a.source === 'user').length >= 2,
  visibleActivities: visibleActivities(state),
  connectionStatus: state.connection.connectionStatus
});

// Apply withConfig first, then connect
const WrappedIntroOverlay = withIsMobile(withConfig(IntroOverlay));
export default connect(mapStateToProps, { sendTextMessage, toggleChatPanelVisible })(
  WrappedIntroOverlay
);



// WEBPACK FOOTER //
// ./src/components/ChatPanel/IntroOverlay/index.js